import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  model,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';

import {
  createAnchorName,
  createPopoverId,
} from '../shared/anchor-positioning';

import {
  TW_DROPDOWN_CONTEXT,
  TwDropdownContext,
  TwDropdownPlacement,
} from './tw-dropdown-context';

/**
 * `tw-dropdown` — parent orchestrator for the dropdown family
 * (TWS-122 Wave 1).
 *
 * Renders no visible chrome itself — it's a coordinator that owns
 * the `open` state, mints the shared `popoverId` + `anchorName`
 * used to wire the native `[popover]` pair, and provides a DI
 * context the three slot children consume:
 *   - `<button twDropdownTrigger>` (attribute directive — the
 *     host must be a real `<button>` because `popovertarget` is
 *     button-specific per the HTML spec);
 *   - `<tw-dropdown-menu>`;
 *   - `<tw-dropdown-item>`.
 *
 * ### Why a parent component instead of just a directive?
 *
 * The native `[popover]` API requires an id+`popovertarget` pair
 * across two DOM elements. We could ask consumers to wire the id
 * themselves, but:
 *
 *   - Every consumer would reinvent the same unique-id minting.
 *   - The `aria-expanded` state on the trigger needs to react to
 *     the menu's native `toggle` event; either we thread an
 *     `@Input() isOpen` across the pair manually, or the parent
 *     mediates once and children read the signal.
 *
 * The parent pattern keeps the consumer markup declarative:
 *
 * ```html
 * <tw-dropdown [(open)]="state.open" placement="bottom">
 *   <button twBtnStyle variant="ghost" size="md" twDropdownTrigger>Menu</button>
 *   <tw-dropdown-menu>
 *     <tw-dropdown-item (selected)="edit()">Edit</tw-dropdown-item>
 *     <tw-dropdown-item (selected)="remove()">Delete</tw-dropdown-item>
 *   </tw-dropdown-menu>
 * </tw-dropdown>
 * ```
 *
 * ### Host styling
 *
 * `display: contents` lets the trigger and menu participate in the
 * consumer's layout as if the `<tw-dropdown>` wasn't there — the
 * parent is purely a logical container. Without `display: contents`
 * the default `display: inline` would break flex/grid parents.
 */
@Component({
  selector: 'tw-dropdown',
  standalone: true,
  template: '<ng-content></ng-content>',
  host: { 'class': 'tw-dropdown', 'style': 'display: contents;' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    // Parent provides its own context object; children inject it
    // via `TW_DROPDOWN_CONTEXT`. `useFactory` + `deps: []` would
    // re-create on every injection — `useExisting` with the
    // component itself as a class-based token would work, but
    // exposing the public methods directly on the component
    // surface pollutes its API. The DI-token + provide-value
    // pattern via `useFactory: () => instance` is idiomatic
    // Angular and matches the `ContextToken` pattern elsewhere in
    // the repo (see `CONTEXT_INSTALLATION_KEY`).
    {
      provide: TW_DROPDOWN_CONTEXT,
      useFactory: (component: TwDropdownComponent) => component.context,
      deps: [TwDropdownComponent],
    },
  ],
})
export class TwDropdownComponent {
  // ---------------------------------------------------------------
  // Signal inputs (public)
  // ---------------------------------------------------------------

  /**
   * Two-way open state. Consumers bind `[(open)]="state.open"` to
   * keep the parent signal in lockstep with the native `toggle`
   * event. `model()` (Angular 17+) combines input + output into a
   * single signal for template brevity.
   */
  readonly open = model<boolean>(false);

  /**
   * Preferred anchor side. The menu's SCSS reads this via a data
   * attribute on the host and selects the corresponding
   * `tw-popover-anchor` mixin call; `position-try-fallbacks`
   * handles auto-flip to the other sides when the preferred side
   * overflows.
   */
  readonly placement = model<TwDropdownPlacement>('bottom');

  // ---------------------------------------------------------------
  // Outputs
  // ---------------------------------------------------------------

  /** Fires once when the menu opens (native `toggle` event's open state). */
  readonly opened = output<void>();
  /** Fires once when the menu closes. */
  readonly closed = output<void>();

  // ---------------------------------------------------------------
  // Internals — stable id pair + trigger/menu references.
  // ---------------------------------------------------------------
  //
  // `createPopoverId()` / `createAnchorName()` draw from module-
  // level monotonic counters, so two dropdowns on the same page
  // get distinct ids. Generated once per instance via readonly
  // fields; they live for the lifetime of the parent.

  private readonly popoverId = createPopoverId();
  private readonly anchorName = createAnchorName();

  /**
   * Trigger's host element, registered via the context's
   * `registerTriggerElement` call. Used to return focus on close.
   * Nullable because registration happens in the trigger's `ngOnInit`
   * (after the parent's effect may have already run once).
   */
  private readonly triggerEl = signal<HTMLElement | null>(null);

  /**
   * Menu's host element (the `[popover]` panel). Registered by the
   * menu for item-focus management.
   */
  private readonly menuEl = signal<HTMLElement | null>(null);

  // Watch `open()` and emit open/close outputs exactly once per
  // transition. Starting with `open() = false`, an initial
  // false→false no-op does NOT fire `closed`; the `previous`
  // tracker guards that.
  constructor() {
    let previous = this.open();
    effect(() => {
      const next = this.open();
      if (next === previous) return;
      previous = next;
      if (next) this.opened.emit();
      else this.closed.emit();
    });
  }

  // ---------------------------------------------------------------
  // Context the DI token hands to children.
  // ---------------------------------------------------------------
  //
  // Built as a readonly object literal so the children see a
  // stable reference; the reactive surface is the signals it
  // exposes. Methods are bound via arrow functions so the
  // children can call them without worrying about `this`
  // context.

  readonly context: TwDropdownContext = {
    popoverId: this.popoverId,
    anchorName: this.anchorName,
    open: computed(() => this.open()),
    placement: computed(() => this.placement()),

    setOpen: (value: boolean) => {
      this.open.set(value);
    },

    requestOpenAndFocusFirst: () => {
      // Opening the menu triggers its native `toggle`; the menu's
      // own `toggle` handler flips `open` via `setOpen` and then
      // focuses the first item. We start that chain by setting
      // `open` true here — the menu's `effect` picks it up and
      // calls `showPopover()` on its host.
      this.open.set(true);
      // Focus-first happens inside the menu's `toggle` handler;
      // the parent doesn't reach in.
    },

    requestClose: () => {
      this.open.set(false);
    },

    requestCloseAndReturnFocus: () => {
      this.open.set(false);
      // Defer focus return until after the close side-effects
      // settle (the menu's `toggle` event fires, the item
      // re-enables its tabindex, etc.). A microtask is enough —
      // we don't need `afterNextRender` because the trigger
      // element's tabindex never changes.
      queueMicrotask(() => {
        this.triggerEl()?.focus();
      });
    },

    registerTriggerElement: (el: HTMLElement) => {
      this.triggerEl.set(el);
    },

    registerMenuElement: (el: HTMLElement) => {
      this.menuEl.set(el);
    },
  };
}
